export const ORG_ADMIN_TABS = {
  MEMBERS: 0,
  INVITATIONS: 1,
  PROJECT_ROLES: 2,
  PROJECTS: 3,
  ORGANIZATION_STRUCTURE: 4,
  SECURITY: 5,
  BRANDING: 6,
  UPLOAD_POLICIES: 7,
  UPLOAD_REPORT: 8,
  RULES_INGESTION: 9,
  CONNECTIONS: 10,
  MODEL_DISCOVERY: 11,
  ACTIVITY_LOG: 12,
  // Appended at the end so existing ?tab= deep links keep their indexes.
  AUDIT_PACKAGES: 13,
  // GUARDIAN: 14 was removed 2026-09-02. It duplicated the Guardian card on
  // Connections — the same connection described in two places, plus signing
  // keys and enrollments a person had to know to look for. All of it now lives
  // in the Guardian connection's own drawer on Connections, as tabs. Index 14
  // is left unused rather than reassigned: a stale ?tab=14 should land nowhere
  // rather than on something unrelated.
  AI_ASSISTANTS: 15,
} as const;

export const SYSTEM_ADMIN_TABS = {
  ORGANIZATIONS: 0,
  SYSTEM_ADMINS: 1,
  SYSTEM_POLICIES: 2,
  SYSTEM_REPORT_TEMPLATES: 3,
  CONFIGURATION: 4,
  USER_JOURNEY: 5,
  ACTIVITY_LOG: 6,
  ROLE_MATRIX: 7,
  // Appended at the end so existing ?tab= deep links keep their indexes.
  AUDIT_PACKAGES: 8,
  FEEDBACK: 9,
} as const;

export const PROJECT_SETTINGS_TABS = {
  GENERAL: 0,
  ALERT_PREFERENCES: 1,
} as const;

export const ORG_ADMIN_TAB_LABELS: Record<number, string> = {
  [ORG_ADMIN_TABS.MEMBERS]: 'Members',
  [ORG_ADMIN_TABS.INVITATIONS]: 'Invitations',
  [ORG_ADMIN_TABS.PROJECT_ROLES]: 'Project Roles',
  [ORG_ADMIN_TABS.PROJECTS]: 'Projects',
  [ORG_ADMIN_TABS.ORGANIZATION_STRUCTURE]: 'Organization Structure',
  [ORG_ADMIN_TABS.SECURITY]: 'Security',
  [ORG_ADMIN_TABS.BRANDING]: 'Branding',
  [ORG_ADMIN_TABS.UPLOAD_POLICIES]: 'Policies',
  [ORG_ADMIN_TABS.UPLOAD_REPORT]: 'Report',
  [ORG_ADMIN_TABS.RULES_INGESTION]: 'Rules',
  [ORG_ADMIN_TABS.CONNECTIONS]: 'Connections',
  [ORG_ADMIN_TABS.MODEL_DISCOVERY]: 'Model Discovery',
  [ORG_ADMIN_TABS.ACTIVITY_LOG]: 'Activity Log',
  [ORG_ADMIN_TABS.AUDIT_PACKAGES]: 'Audit Packages',
  [ORG_ADMIN_TABS.AI_ASSISTANTS]: 'AI Assistants',
};

/**
 * Required capability per org-admin tab index.
 * null = no capability guard (visible to all users with dashboard access).
 * Used as the single source of truth for both the left-nav per-item guards
 * and the org-admin dashboard per-tab disabled state.
 */
export const ORG_ADMIN_TAB_CAPABILITY: Record<number, string | null> = {
  [ORG_ADMIN_TABS.MEMBERS]: 'org:manage_members',
  [ORG_ADMIN_TABS.INVITATIONS]: 'org:manage_members',
  [ORG_ADMIN_TABS.PROJECT_ROLES]: 'org:manage_members',
  [ORG_ADMIN_TABS.PROJECTS]: 'org:view_activity_log',
  [ORG_ADMIN_TABS.ORGANIZATION_STRUCTURE]: 'org:manage_settings',
  [ORG_ADMIN_TABS.SECURITY]: 'org:manage_settings',
  [ORG_ADMIN_TABS.BRANDING]: 'org:manage_settings',
  [ORG_ADMIN_TABS.UPLOAD_POLICIES]: 'org:manage_policies',
  [ORG_ADMIN_TABS.UPLOAD_REPORT]: 'report:manage_org',
  [ORG_ADMIN_TABS.RULES_INGESTION]: 'org:manage_policies',
  [ORG_ADMIN_TABS.CONNECTIONS]: 'org:manage_settings',
  [ORG_ADMIN_TABS.MODEL_DISCOVERY]: 'org:manage_settings',
  [ORG_ADMIN_TABS.ACTIVITY_LOG]: 'org:view_activity_log',
  [ORG_ADMIN_TABS.AUDIT_PACKAGES]: 'org:manage_policies',
  [ORG_ADMIN_TABS.AI_ASSISTANTS]: 'org:view_activity_log',
};

/**
 * Any of these capabilities is sufficient to enter the org-admin dashboard
 * and see the "Company Admin" nav group.
 *
 * NOTE: `org:create_project` is intentionally excluded here. Members hold
 * that capability so they can create projects from the main dashboard, but
 * they should not see the org-admin settings section.
 */
/** Org-admin tabs only beta users see, on top of their capability. */
export const ORG_ADMIN_BETA_TABS: ReadonlySet<number> = new Set([
  ORG_ADMIN_TABS.AI_ASSISTANTS,
]);

export const ORG_ADMIN_TAB_ENTRY_CAPABILITIES: string[] = [
  'org:manage_members',
  'org:manage_policies',
  'org:manage_settings',
  'org:view_activity_log',
  'report:manage_org',
];

export const SYSTEM_ADMIN_TAB_LABELS: Record<number, string> = {
  0: 'Organizations',
  1: 'System Admins',
  2: 'System Policies',
  3: 'System Report Templates',
  4: 'Configuration',
  5: 'User Journey',
  6: 'Activity Log',
  7: 'Role Matrix',
  8: 'System Audit Packages',
  9: 'Feedback',
};

export const PROJECT_SETTINGS_TAB_LABELS: Record<number, string> = {
  0: 'General',
  1: 'Alert Preferences',
};

export const PERSONAL_SETTINGS_TABS = {
  APPEARANCE: 0,
  NOTIFICATIONS: 1,
  ACCESS_TOKENS: 2,
  TWO_FA: 3,
  RESET_PASSWORD: 4,
  MY_PERMISSIONS: 5,
  RAI_TRAINING: 6,
} as const;

export const PERSONAL_SETTINGS_TAB_LABELS: Record<number, string> = {
  0: 'Appearance',
  1: 'Notifications',
  2: 'Access Tokens',
  3: '2FA',
  4: 'Reset Password',
  5: 'My Permissions',
  6: 'RAI Training',
};
